import { useCallback } from "react";
import { Alert, Card, ListGroup } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { TagMultiUpdateError, TagMultiUpdateResponse } from "./domain/TagMultiUpdateResponse";
import { messages } from "./TagMultiUpdateResult.messages";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 1rem;
`;

const Error = styled.div`
  display: flex;
  flex-direction: column;
`;

export function TagMultiUpdateResult({ result }: { result: TagMultiUpdateResponse }) {
  return (
    <Container>
      <Card>
        <Card.Header as="h5">
          <FormattedMessage {...messages.header} />
        </Card.Header>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <FormattedMessage {...messages.updatedTags} />:{` ${result.updatedTags}`}
          </ListGroup.Item>
          <ListGroup.Item>
            <FormattedMessage {...messages.removedTags} />:{` ${result.deletedTags}`}
          </ListGroup.Item>
          <ListGroup.Item>
            <FormattedMessage {...messages.unchangedTags} />:{` ${result.unchangedTags}`}
          </ListGroup.Item>
          <ListGroup.Item>
            <FormattedMessage {...messages.errors} />:{` ${result.errors?.length ?? 0}`}
          </ListGroup.Item>
        </ListGroup>
      </Card>
      {result.errors.length > 0 && (
        <Card>
          <Card.Header as="h5">
            <FormattedMessage {...messages.headerErrors} />
          </Card.Header>
          <ErrorContainer>
            <Alert variant="danger">
              <strong>
                <FormattedMessage {...messages.alertSomeErrors} />
              </strong>
            </Alert>
            <ListGroup variant="flush">
              {result.errors.map(error => (
                <ListGroup.Item key={error.tagTicker}>
                  <TagError error={error} />
                </ListGroup.Item>
              ))}
            </ListGroup>
          </ErrorContainer>
        </Card>
      )}
    </Container>
  );
}

function TagError({ error }: { error: TagMultiUpdateError }) {
  const { formatMessage } = useIntl();
  const errorMessage = useCallback(() => {
    switch (error.errorCode) {
      case "TAG_ASSOCIATED":
        return formatMessage(messages.errorUsedTagDescription, {
          tagTicker: error.tagTicker,
          relatedTicker: error.errorContext?.["associatedTicker"],
        });
      case "MISSING_TRANSLATION":
        return formatMessage(messages.errorMissingTranslationDescription, {
          locales: error.errorContext?.["locales"],
        });
      case "DUPLICATED_TICKER":
        return formatMessage(messages.errorRepeatedTagDescription, {
          tagTicker: error.tagTicker,
          relatedTicker: error.relatedTicker,
        });
      case "PROPERTY_TICKER_WITHOUT_PERMISSION":
        return formatMessage(messages.errorPropertyTickerWithoutPermission, {
          ticker: error.relatedTicker,
        });
      case "PROPERTY_TICKER_NOT_FOUND":
        return formatMessage(messages.errorPropertyTickerNotFound, {
          ticker: error.relatedTicker,
        });
      case "INVALID_TICKER":
        return formatMessage(messages.errorInvalidTicker, {
          ticker: error.tagTicker,
        });
      default:
        return "";
    }
  }, [error, formatMessage]);

  return (
    <Error>
      <span>
        <FormattedMessage
          {...messages.errorEngineTickerTitle}
          values={{ ticker: error.relatedTicker }}
        />
      </span>
      <span>
        <FormattedMessage {...messages.errorTagTickerTitle} values={{ ticker: error.tagTicker }} />
      </span>
      <span>
        <FormattedMessage {...messages.errorDescriptionTitle} values={{ error: errorMessage() }} />
      </span>
    </Error>
  );
}
